import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Switch } from "@/shared/ui/switch";
import { SubscriptionField } from "./SubscriptionField";
import { VipField } from "./VipField";
import { PlanField } from "./PlanField";
import { TransactionsField } from "./TransactionsField";
import { useCustomMutation } from "@/shared/hooks/useCustomMutation";
import { useCustomQuery } from "@/shared/hooks/useCustomQuery";
import ProfilesService from "@/features/profiles/services/profiles/profileService";
import { IUserById, IUserUpdatePayload, UserRole, EmployeeRole } from "@/features/profiles/services/profiles/types";
import { z } from "zod";
import { AxiosError } from "axios";
import { ModalDeleteCreatorWarning } from "./ModalDeleteCreatorWarning";
import { Select } from "@/shared/widgets/select";
import { useToast } from "@/shared/hooks/use-toast";
import { PageContent } from "@/shared/widgets/layout/PageContent";
import CreatorService from "@/features/creators/services/creatorService";
import { useTranslation } from "@/shared/i18n";
import { Loader2 } from "lucide-react";
import { TranslationsField } from "@/shared/widgets/translations-field/TranslationsField";
import { TranslationItem } from "@/shared/widgets/translations-field/types";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/shared/ui/tabs";
import { Card, CardContent } from "@/shared/ui/card";
import { Separator } from "@/shared/ui/separator";

export default function UserEdit() {
  const { t } = useTranslation('profiles');
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const userEditSchema = z
    .object({
      email: z.string().email(t('invalidEmail')),
      username: z.string().optional(),
      isCreator: z.boolean().default(false),
      mottoTranslations: z.array(z.object({
        languageCode: z.string(),
        description: z.string(),
      })).optional(),
      dynamicLink: z.string().optional(),
      vip: z.boolean(),
      vipExpiresAt: z.string().nullable(),
    })
    .superRefine((data, ctx) => {
      if (data.vip && !data.vipExpiresAt) {
        ctx.addIssue({
          path: ["vipExpiresAt"],
          code: z.ZodIssueCode.custom,
          message: t('vipExpirationRequired'),
        });
      }
    });

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [isCreator, setIsCreator] = useState(false);
  const [wasCreator, setWasCreator] = useState(false); // 👈 guarda estado inicial
  const [mottoTranslations, setMottoTranslations] = useState<TranslationItem[]>([
    { languageCode: "PT_BR", description: "" },
  ]);
  const [dynamicLink, setDynamicLink] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [vip, setVip] = useState(false);
  const [vipExpiresAt, setVipExpiresAt] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [role, setRole] = useState<UserRole>("USER");
  const [employeeRole, setEmployeeRole] = useState<EmployeeRole | null>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [creatorId, setCreatorId] = useState<string>("");

  useEffect(() => {
    return () => {
      if (avatarPreview && avatarPreview.startsWith("blob:")) URL.revokeObjectURL(avatarPreview);
      if (coverPreview && coverPreview.startsWith("blob:")) URL.revokeObjectURL(coverPreview);
    };
  }, []);

  const { mutateAsync: updateProfileGeneral, isPending: isUpdatingGeneral } = useCustomMutation({
    serviceInstance: ProfilesService.Instance,
    selectAction: (s) => s.updateProfileGeneral,
  });

  const { mutateAsync: updateProfileCreator, isPending: isUpdatingCreator } = useCustomMutation({
    serviceInstance: ProfilesService.Instance,
    selectAction: (s) => s.updateProfileCreator,
  });

  const { mutateAsync: updateProfileEmployee, isPending: isUpdatingEmployee } = useCustomMutation({
    serviceInstance: ProfilesService.Instance,
    selectAction: (s) => s.updateProfileEmployee,
  });

  const isPending = isUpdatingGeneral || isUpdatingCreator || isUpdatingEmployee;

  const { data: fetchedUser, isLoading: isFetching } = useCustomQuery({
    serviceInstance: ProfilesService.Instance,
    selectAction: (s) => s.getProfileById,
    parameter: { id: id as string },
    options: { enabled: Boolean(id) },
  });

  const { data: creatorPacks, refetch: refetchCreatorPacks } = useCustomQuery({
    serviceInstance: CreatorService.Instance,
    selectAction: (s) => s.getPacksByCreator,
    parameter: { id: creatorId },
    options: { enabled: false },
  });

  useEffect(() => {
    if (!fetchedUser) return;

    setEmail((fetchedUser as any).email ?? "");
    setVip(Boolean(fetchedUser.vip));
    setVipExpiresAt(fetchedUser.vipExpiresAt ? new Date(fetchedUser.vipExpiresAt) : null);

    const subscription: IUserById["subscription"] = fetchedUser.subscription;
    setSubscribed(Boolean(subscription?.active));
    setExpiresAt(subscription?.expiresAt ? new Date(subscription.expiresAt) : null);

    setName((fetchedUser as any).name ?? "");
    setUsername((fetchedUser as any).username ?? "");

    const creatorData = fetchedUser.creatorData;
    const initialCreator = Boolean(creatorData?.isActive);
    setIsCreator(initialCreator);
    setWasCreator(initialCreator); // 👈 salva estado inicial
    setCreatorId(creatorData?.packsCreators?.[0]?.creatorId ?? "");
    setRole(creatorData?.role ?? "USER");
    setEmployeeRole((fetchedUser as any).employees?.roles?.[0] ?? null);

    const translations = creatorData?.translations as Array<{ languageCode: string; motto: string }> | undefined;
    if (translations && translations.length > 0) {
      setMottoTranslations(
        translations.map((t) => ({
          languageCode: t.languageCode as TranslationItem["languageCode"],
          description: t.motto ?? "",
        }))
      );
    }
    setDynamicLink(creatorData?.promoLinks?.[0]?.url ?? "");

    const sanitize = (v?: string | null) => {
      if (!v) return null;
      const s = String(v).trim();
      if (!s || s.toLowerCase() === "null" || s.toLowerCase() === "undefined") return null;
      return s;
    };
    setAvatarPreview(sanitize((fetchedUser as any).avatar) ?? sanitize(creatorData?.avatar) ?? null);
    setCoverPreview(sanitize(creatorData?.cover) ?? null);
  }, [fetchedUser]);

  const handleDisableCreator = async () => {
    const { data } = await refetchCreatorPacks(); // 👈 busca só agora

    if (data && data.length > 0) {
      setShowDeleteUserModal(true);
      return;
    }

    setIsCreator(false);
  };
  const handleSave = async () => {
    // 👇 bloqueia desativação se houver packs ativos
    if (wasCreator && !isCreator) {
      if (creatorPacks && creatorPacks.length > 0) {
        setShowDeleteUserModal(true);
        return;
      }
    }

    const newUser = {
      email,
      username,
      isCreator,
      mottoTranslations,
      dynamicLink,
      vip,
      role: role as UserRole,
      vipExpiresAt: vipExpiresAt?.toISOString() ?? null,
      subscription: {
        active: subscribed,
        expiresAt,
      },
    };

    const parsed = userEditSchema.safeParse(newUser);
    if (!parsed.success) {
      const fieldErrors = parsed.error.formErrors.fieldErrors;
      const formattedErrors: Record<string, string> = {};
      for (const key in fieldErrors) {
        if (fieldErrors[key]?.length) {
          formattedErrors[key] = fieldErrors[key]![0];
        }
      }
      setFormErrors(formattedErrors);
      return;
    }

    setFormErrors({});
    setIsLoading(true);

    try {
      if (id) {
        const promises = [];

        // 1. Atualizar dados gerais
        promises.push(
          updateProfileGeneral({
            id,
            vip,
            vipExpiresAt: vipExpiresAt?.toISOString() ?? null,
            username,
            name,
          })
        );

        // 2. Atualizar creator (se for creator ou se era e deixou de ser)
        if (isCreator || wasCreator) {
          promises.push(
            updateProfileCreator({
              id,
              isActive: isCreator,
              ...(isCreator && {
                role,
                name,
                username,
                dynamicLink,
                mottoTranslations: mottoTranslations
                  .filter((t) => t.description.trim())
                  .map((t) => ({ languageCode: t.languageCode, motto: t.description })),
              }),
            })
          );
        }

        // 3. Atualizar employee
        promises.push(
          updateProfileEmployee({
            id,
            employeeRole,
          })
        );

        await Promise.all(promises);

        // 4. Upload do avatar (PATCH gera URL pré-assinada, depois envia a foto ao storage)
        if (avatarFile) {
          const uploadAvatarAction = ProfilesService.Instance.uploadAvatar.actionBuilder({
            profileId: id,
            file: avatarFile,
          });
          await uploadAvatarAction();
        }

        // 5. Upload da capa SÓ DEPOIS de garantir que o creator existe
        if (coverFile && isCreator) {
          const uploadAction = ProfilesService.Instance.uploadCover.actionBuilder({
            profileId: id,
            file: coverFile,
          });
          await uploadAction();
        }

        toast({
          title: t('profileUpdated'),
          description: t('profileUpdatedDescription'),
        });
        queryClient.invalidateQueries({ queryKey: ["get-profile-by-id"] });
      }
    } catch (err) {
      toast({
        title: t('errorUpdatingProfile'),
        description: (err as Error).message ?? t('errorUpdatingProfile'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    const prevPageUrl = location.state?.from;
    navigate(prevPageUrl || "/users");
  };

  useEffect(() => {
    if (vip && !vipExpiresAt) {
      const futureDate = new Date();
      futureDate.setFullYear(futureDate.getFullYear() + 100);
      setVipExpiresAt(futureDate);
    }
  }, [vip, vipExpiresAt]);

  useEffect(() => {
    if (subscribed && !expiresAt) {
      const futureDate = new Date();
      futureDate.setFullYear(futureDate.getFullYear() + 1);
      setExpiresAt(futureDate);
    }
  }, [subscribed, expiresAt]);



  return (
    <PageContent title={t('editUser')} items={[
      { label: t('users'), href: "/users" },
      { label: t('editUser'), href: "/users/edit" },
    ]}>
      <ModalDeleteCreatorWarning
        isOpen={showDeleteUserModal && creatorPacks?.length > 0}
        onClose={() => setShowDeleteUserModal(false)}
        pendingItems={creatorPacks ?? []}
      />

      {isFetching ? (
        <Card className="bg-white/[0.03] border-white/[0.06]">
          <CardContent className="flex flex-col items-center justify-center py-20 gap-4 p-6">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t('loading')}</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white/[0.03] border-white/[0.06]">
          <CardContent className="p-6 space-y-6">
            <Tabs defaultValue="user">
              <TabsList>
                <TabsTrigger value="user">{t('user')}</TabsTrigger>
                <TabsTrigger value="creator">Creator</TabsTrigger>
                <TabsTrigger value="admin">Admin</TabsTrigger>
              </TabsList>

              {/* ── Aba Usuário ── */}
              <TabsContent value="user" className="space-y-6 mt-6">
                {/* Dados Pessoais */}
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-4">{t('personalData')}</h3>
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Avatar */}
                    <div className="flex-shrink-0 flex flex-col items-center gap-3">
                      <div className="relative w-32 h-32 rounded-full overflow-hidden ring-2 ring-white/10 border border-white/10 bg-muted">
                        {avatarPreview ? (
                          <img
                            src={avatarPreview}
                            alt={t('avatar')}
                            className="w-full h-full object-cover"
                            onError={() => setAvatarPreview(null)}
                          />
                        ) : (
                          <div
                            className="w-full h-full flex flex-col items-center justify-center text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                            onClick={() => avatarInputRef.current?.click()}
                          >
                            <span className="text-3xl leading-none mb-1">+</span>
                            <span className="text-xs">{t('noPhoto')}</span>
                          </div>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => avatarInputRef.current?.click()}
                      >
                        {avatarPreview ? t('changePhoto') : t('addPhoto')}
                      </Button>
                      <input
                        ref={avatarInputRef}
                        id="avatar"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0] ?? null;
                          setAvatarFile(file);
                          setAvatarPreview(file ? URL.createObjectURL(file) : avatarPreview);
                        }}
                      />
                    </div>

                    {/* Campos */}
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="email">{t('email')}</Label>
                        <Input
                          id="email"
                          readOnly
                          placeholder={email}
                          className="opacity-50 cursor-not-allowed"
                        />
                        {formErrors.email && (
                          <p className="text-sm text-destructive">{formErrors.email}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="generalName">{t('name')}</Label>
                        <Input
                          id="generalName"
                          value={name}
                          placeholder={t('yourName')}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="generalUsername">{t('username')}</Label>
                        <Input
                          id="generalUsername"
                          value={username}
                          placeholder={t('username')}
                          onChange={(e) => setUsername(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Assinatura & VIP */}
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-4">{t('subscriptionAndVip')}</h3>
                  <div className="space-y-6">
                    <SubscriptionField
                      formErrors={formErrors}
                      subscribed={subscribed}
                      onSubscribedChange={setSubscribed}
                      expiresAt={expiresAt}
                      onExpiresAtChange={setExpiresAt}
                    />
                    <PlanField plan={fetchedUser?.subscription?.plan} />
                    <VipField
                      formErrors={formErrors}
                      vip={vip}
                      onVipChange={setVip}
                      vipExpiresAt={vipExpiresAt}
                      onVipExpiresAtChange={setVipExpiresAt}
                    />
                  </div>
                </div>
              </TabsContent>

              {/* ── Aba Creator ── */}
              <TabsContent value="creator" className="space-y-6 mt-6">
                <div className="flex items-center justify-between rounded-lg border border-border bg-card p-4">
                  <div>
                    <Label htmlFor="isCreator">{t('isCreator')}</Label>
                    <p className="text-xs text-muted-foreground">{t('creatorAccess')}</p>
                  </div>
                  <Switch
                    id="isCreator"
                    checked={isCreator}
                    onCheckedChange={(checked) => {
                      if (!checked) {
                        handleDisableCreator();
                      } else {
                        setIsCreator(true);
                      }
                    }}
                  />
                </div>

                {isCreator && (
                  <>
                    {/* Capa */}
                    <div className="relative rounded-2xl overflow-hidden border border-white/10">
                      <div className="relative h-48 md:h-56 w-full bg-black/30">
                        {coverPreview ? (
                          <img
                            src={coverPreview}
                            alt="Capa"
                            className="absolute inset-0 w-full h-full object-cover"
                            onError={() => setCoverPreview(null)}
                          />
                        ) : (
                          <div
                            className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground cursor-pointer"
                            onClick={() => coverInputRef.current?.click()}
                            aria-label={t('addCover')}
                            title={t('addCover')}
                          >
                            <div className="absolute inset-3 rounded-xl border-2 border-dashed border-white/20" />
                            <span className="text-xs">{t('noPhoto')}</span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="absolute bottom-4 right-4 bg-black/50 border-white/20 backdrop-blur-sm"
                          onClick={() => coverInputRef.current?.click()}
                        >
                          {coverPreview ? t('changeCover') : t('addCover')}
                        </Button>
                      </div>
                    </div>

                    <input
                      ref={coverInputRef}
                      id="cover"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        setCoverFile(file);
                        setCoverPreview(file ? URL.createObjectURL(file) : coverPreview);
                      }}
                    />

                    {/* Campos do Creator */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="role">{t('creatorProfileType')}</Label>
                        <Select
                          placeholder={t('selectProfile')}
                          value={role}
                          onChange={(e) => setRole(e as UserRole)}
                          data={[
                            { value: "INFLUENCER", label: t('influencer') },
                            { value: "PHOTOGRAPHER", label: t('photographer') },
                          ]}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <TranslationsField
                          value={mottoTranslations}
                          onChange={setMottoTranslations}
                          label={t('about')}
                          placeholder={t('shortPhraseAboutYou')}
                          minHeight="60px"
                        />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label htmlFor="dynamicLink">{t('customLink')}</Label>
                        <Input
                          id="dynamicLink"
                          value={dynamicLink}
                          onChange={(e) => setDynamicLink(e.target.value)}
                          placeholder={t('customLinkPlaceholder')}
                        />
                      </div>
                    </div>
                  </>
                )}
              </TabsContent>

              {/* ── Aba Admin ── */}
              <TabsContent value="admin" className="space-y-6 mt-6">
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-4">{t('employeeProfile')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="employeeRole">{t('employeeRole')}</Label>
                      <Select
                        placeholder={t('selectEmployeeRole')}
                        value={employeeRole ?? "NONE"}
                        onChange={(e) => setEmployeeRole(e === "NONE" ? null : (e as EmployeeRole))}
                        data={[
                          { value: "NONE", label: t('none') },
                          { value: "ADMIN", label: t('admin') },
                          { value: "SUPPORT", label: t('support') },
                          { value: "FINANCIAL", label: t('finance') },
                          { value: "READ_ONLY", label: t('readOnly') },
                          { value: "MARKETING", label: t('marketing') },
                        ]}
                      />
                    </div>
                  </div>
                </div>

                {fetchedUser?.transactions && fetchedUser.transactions.length > 0 && (
                  <>
                    <Separator />
                    <TransactionsField transactions={fetchedUser.transactions} />
                  </>
                )}
              </TabsContent>
            </Tabs>

            <Separator />

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={isPending || isLoading}
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={handleSave}
                disabled={isPending || isLoading}
              >
                {(isPending || isLoading) ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('saving')}
                  </>
                ) : (
                  t('save')
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContent>
  );
}
